import React, { useEffect } from "react";
import { Modal, Button, Divider, Row, Col } from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  ExclamationCircleTwoTone,
  DeleteOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { RoutePaths } from "../../routes/route";
import { useQueryNavigator } from "../../utils/navigate";

interface ModalProps {
  type:
    | "success"
    | "error"
    | "confirm"
    | "activate"
    | "form"
    | "warning"
    | "warning-incomplete"
    | "timeout";
  title: string;
  message?: React.ReactNode;
  isVisible: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  children?: React.ReactNode;
  autoClose?: boolean;
  duration?: number;
  closeable?: boolean;
  successButtonText?: string;
  onSuccessClick?: () => void;
  cancelButtonText?: string;
  onCancelClick?: () => void;
}

const CustomModal: React.FC<ModalProps> = ({
  type,
  title,
  message,
  isVisible,
  onClose,
  onConfirm,
  children,
  autoClose = false,
  duration = 3000,
  closeable = false,
  successButtonText,
  onSuccessClick,
  cancelButtonText,
  onCancelClick,
}) => {
  useEffect(() => {
    if (autoClose && isVisible) {
      const timer = setTimeout(() => {
        if (onClose) onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [autoClose, isVisible, onClose, duration]);
  const navigateWithQuery = useQueryNavigator();
  const isDashboard =
    window.location.pathname === RoutePaths.IFRAME_TARGET_MARKETING_HOME;

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={null}
      centered
      maskClosable={false}
      closable={closeable}
      zIndex={9999}
    >
      <div style={{ textAlign: "center", padding: "20px" }}>
        {type !== "form" && (
          <>
            {type === "success" ? (
              <CheckCircleFilled
                style={{ fontSize: "60px", color: "#27AE60" }}
              />
            ) : type === "error" ? (
              <CloseCircleTwoTone
                twoToneColor="#E61D2B"
                style={{ fontSize: "60px" }}
              />
            ) : type === "warning" || type === "warning-incomplete" ? (
              <ExclamationCircleTwoTone
                twoToneColor="#faad14"
                style={{ fontSize: "60px" }}
              />
            ) : type === "activate" ? (
              <CheckCircleOutlined
                style={{ fontSize: "60px", color: "#27AE60" }}
              />
            ) : type === "timeout" ? (
              <ExclamationCircleTwoTone
                twoToneColor="#1890ff"
                style={{ fontSize: "60px" }}
              />
            ) : (
              <DeleteOutlined style={{ fontSize: "60px", color: "#E61D2B" }} />
            )}
            <h2 style={{ fontWeight: 600, marginTop: 16 }}>{title}</h2>
            {message && (
              <p style={{ color: "#6c757d", fontSize: "14px" }}>
                {message}
                {type === "confirm" && (
                  <>
                    <br />
                    <span style={{ color: "#6c757d", fontSize: "14px" }}>
                      This action cannot be undone
                    </span>
                  </>
                )}
              </p>
            )}
          </>
        )}

        {type === "form" && children}

        {type === "confirm" ||
        type === "warning" ||
        type === "warning-incomplete" ? (
          <>
            <Divider />
            <Row justify="center" gutter={10}>
              <Col>
                <Button
                  size="large"
                  onClick={onClose}
                  style={{
                    height: 50,
                    width: 200,
                    fontSize: 15,
                  }}
                >
                  Cancel
                </Button>
              </Col>
              <Col>
                <Button
                  size="large"
                  type="primary"
                  danger={type === "confirm"}
                  onClick={onConfirm}
                  style={{
                    height: 50,
                    width: 200,
                    fontSize: 15,
                  }}
                >
                  {type === "warning"
                    ? "Save Settings"
                    : type === "warning-incomplete"
                    ? "Continue Editing"
                    : "Yes, Delete"}
                </Button>
              </Col>
            </Row>
          </>
        ) : null}
        {(successButtonText || cancelButtonText) && (
          <>
            <Divider />
            <Row gutter={[12, 12]}>
              <Col span={successButtonText && cancelButtonText ? 12 : 24}>
                <Button
                  size="large"
                  style={{
                    width: "100%",
                  }}
                  onClick={onCancelClick}
                >
                  {cancelButtonText}
                </Button>
              </Col>
              <Col span={successButtonText && cancelButtonText ? 12 : 24}>
                <Button
                  size="large"
                  type="primary"
                  style={{
                    width: "100%",
                  }}
                  onClick={onSuccessClick}
                >
                  {successButtonText}
                </Button>
              </Col>
            </Row>
          </>
        )}

        {type === "form" && (
          <button
            onClick={onClose}
            style={{
              marginTop: "20px",
              padding: "10px 24px",
              background: "#52c41a",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              fontSize: "16px",
              cursor: "pointer",
            }}
          >
            Ok
          </button>
        )}

        {type === "timeout" && (
          <>
            <Divider />
            <Button
              size="large"
              type="primary"
              style={{ width: 200 }}
              onClick={() => {
                if (isDashboard) {
                  window.location.reload();
                } else {
                  navigateWithQuery(
                    RoutePaths.IFRAME_TARGET_MARKETING_HOME,
                    "id",
                    null
                  );
                }
              }}
            >
              {isDashboard ? "Reload Page" : "Return to Home"}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CustomModal;
